import { creditsProgress } from './credits-bar-color';

/**
 * The "earn credits instead" offer shown at the out-of-credits moments — WHETHER to show it, and
 * nothing else. Import-free by design (only the pinned usage-band helper), so the rule can be
 * exercised directly in a node test without dragging React, next/router or the data layer in.
 *
 * Three entry points, one decision:
 *
 *  - `sidebar_credits` — the rail's / drawer's credits card, while the usage bar is in the RED band.
 *    The band is `creditsProgress` (lib/credits-bar-color.ts), the SAME helper the bar itself paints
 *    from — the thresholds are a pinned cross-platform contract and must never be re-derived here.
 *  - `routine_paused`  — a routine parked because the account ran out of credits (`'outOfCredits'`).
 *  - `buy_credits`     — the Max+ top-up modal/sheet, where there is no upgrade left to sell.
 *
 * Everything is behind ONE gate (`symphonyOutOfCreditsEarnOfferEnabled`), and it FAILS CLOSED: an
 * unresolved conduct leaves `gateEnabled` false, so this is both a ramp and a kill switch. The offer
 * is deliberately secondary everywhere — Upgrade/Check out stay primary and keep their own BI, so
 * the conversion baseline this is measured against is untouched.
 */

/** The BI `entry_point` additional field — one value per surface. */
export type EarnOfferEntryPoint = 'sidebar_credits' | 'routine_paused' | 'buy_credits';

export type EarnOfferEntry =
  /** `fraction` is credits SPENT over the cap, 0–1 — exactly what the progress bar renders. */
  | { kind: 'sidebar_credits'; fraction: number }
  | { kind: 'routine_paused'; runState: string }
  | { kind: 'buy_credits' };

export function earnOfferEntryPoint(entry: EarnOfferEntry): EarnOfferEntryPoint {
  return entry.kind;
}

export function showEarnCreditsOffer(gateEnabled: boolean, entry: EarnOfferEntry): boolean {
  if (!gateEnabled) return false;
  switch (entry.kind) {
    case 'sidebar_credits':
      // Red band only: an account with credits to spend is not an out-of-credits moment.
      return creditsProgress(entry.fraction) === 'destructive';
    case 'routine_paused':
      return entry.runState === 'outOfCredits';
    case 'buy_credits':
      // The user opened the top-up sheet — they are asking for credits by definition.
      return true;
    default:
      return false;
  }
}
